import { useCallback, useState } from 'react';
import { useMedplum } from '@medplum/react';
import { useTranslation } from 'react-i18next';
import { notifications } from '@mantine/notifications';
import { Practitioner } from '@medplum/fhirtypes';

// -----------------------------------------------------------------------------
interface UsePractitionerDeletionReturn {
  deletePractitioner: (practitioner: Practitioner) => Promise<boolean>;
  deleting: boolean;
}

// -----------------------------------------------------------------------------
export const usePractitionerDeletion = (): UsePractitionerDeletionReturn => {
  const medplum = useMedplum();
  const { t } = useTranslation(['admin', 'common']);
  const [deleting, setDeleting] = useState(false);

  // ---------------------------------------------------------------------------
  const deletePractitioner = useCallback(
    async (practitioner: Practitioner): Promise<boolean> => {
      if (!practitioner?.id) {
        return false;
      }

      setDeleting(true);
      try {
        await medplum.deleteResource('Practitioner', practitioner.id);

        notifications.show({
          color: 'green',
          title: t('success', { ns: 'common' }),
          message: t('userManagement.notifications.deleteSuccess', {
            ns: 'admin',
          }) as string,
        });

        return true;
      } catch (error) {
        console.error('Error deleting practitioner:', error);
        notifications.show({
          color: 'red',
          title: t('error', { ns: 'common' }),
          message: t('userManagement.notifications.deleteError', {
            ns: 'admin',
          }),
        });

        return false;
      } finally {
        setDeleting(false);
      }
    },
    [medplum, t]
  );

  // ---------------------------------------------------------------------------
  return {
    deletePractitioner,
    deleting,
  };
};
